import { useStreamingReport } from '../../hooks/useStreamingReport';
import { siteConfig } from '../../config/site';
import { Button, Form, Select, ConfigProvider, message, Input, Tooltip, Checkbox, Modal } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { ArrowLeftOutlined, QuestionCircleOutlined, GiftOutlined } from '@ant-design/icons';
import React from 'react';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/zh-cn';
import { SolarDay, SolarTime } from 'tyme4ts';
import { Solar } from 'lunar-typescript';
import * as iztro from 'iztro';
import BirthDateTimePicker from '../../components/BirthDateTimePicker';
import { CITY_OPTIONS, cityFilterOption } from '../../data/cities';
import { calculateDaYunWithAge, buildZiWeiData, isWeChatBrowser } from '../../utils';
import type { City } from '../../data/cities';
import AILoadingAnimation from '../../components/AILoadingAnimation';
import HepanReport, { decompressData } from '../HepanReport';
import WeChatGuide from '../../components/WeChatGuide';
import './index.css';

dayjs.locale('zh-cn');
dayjs.extend(dayOfYear);

// MBTI 选项列表
const MBTI_OPTIONS = [
    { value: 'INTJ', label: 'INTJ 建筑师' },
    { value: 'INTP', label: 'INTP 逻辑学家' },
    { value: 'ENTJ', label: 'ENTJ 指挥官' },
    { value: 'ENTP', label: 'ENTP 辩论家' },
    { value: 'INFJ', label: 'INFJ 提倡者' },
    { value: 'INFP', label: 'INFP 调停者' },
    { value: 'ENFJ', label: 'ENFJ 主人公' },
    { value: 'ENFP', label: 'ENFP 竞选者' },
    { value: 'ISTJ', label: 'ISTJ 检查员' },
    { value: 'ISFJ', label: 'ISFJ 保护者' },
    { value: 'ESTJ', label: 'ESTJ 总经理' },
    { value: 'ESFJ', label: 'ESFJ 执政官' },
    { value: 'ISTP', label: 'ISTP 鉴赏家' },
    { value: 'ISFP', label: 'ISFP 探险家' },
    { value: 'ESTP', label: 'ESTP 企业家' },
    { value: 'ESFP', label: 'ESFP 表演者' },
];

// 将标准时间转换为真太阳时（支持分钟精度）
function toTrueSolarTimeWithMinutes(datetime: dayjs.Dayjs, longitude: number, timeZoneOffset: number = 8): dayjs.Dayjs {
    const n = datetime.dayOfYear();
    const B = (2 * Math.PI * (n - 81)) / 364;
    const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
    const zoneLongitude = 15 * timeZoneOffset;
    const deltaLon = 4 * (longitude - zoneLongitude);
    const deltaMinutes = eot + deltaLon;
    return datetime.add(deltaMinutes, 'minute');
}

// 将 24 小时制转换为关系结构模型时辰索引 (0-12)
function hourToShiChenIndex(hour: number): number {
    return Math.floor((hour + 1) / 2);
}

// 同步计算关系结构模型数据
function calculateZiWeiData(birthday: string, birthTime: number, gender: '男' | '女') {
    try {
        // 使用 iztro 的 withOptions 方法进行同步计算（使用默认配置）
        const astrolabe = iztro.astro.withOptions({
            type: 'solar',
            dateStr: birthday,
            timeIndex: birthTime,
            gender: gender,
            fixLeap: true,
            language: 'zh-CN'
        });
        // 计算本分析图谱 + 未来三年年度趋势（2026丙午、2027丁未、2028戊申）
        return buildZiWeiData(astrolabe, birthTime, [2026, 2027, 2028]);
    } catch (error) {
        console.error('关系结构计算失败:', error);
        return null;
    }
}

export const Hepan: React.FC = () => {
    const [form] = Form.useForm();
    const [aiLoading, setAiLoading] = React.useState(false);
    const { run: runReportStream, streaming: reportStreaming, error: streamError, reportToken: streamReportToken, access: reportAccess } = useStreamingReport();
    const [reportToken, setReportToken] = React.useState('');
    const [reportRedeemed, setReportRedeemed] = React.useState(false);
    const [isFollowUpLoading, setIsFollowUpLoading] = React.useState(false);

    // 报告数据
    const [person1Data, setPerson1Data] = React.useState<any>(null);
    const [person2Data, setPerson2Data] = React.useState<any>(null);
    const [aiAnswer, setAiAnswer] = React.useState<string>('');
    const [relationshipType, setRelationshipType] = React.useState<string>('');
    const [showReport, setShowReport] = React.useState(false);
    const [compressedReport, setCompressedReport] = React.useState<string>(''); // 后端返回的压缩报告数据

    // 邀请码状态
    const [inviteCode, setInviteCode] = React.useState('');
    const [isInviteCodeValid, setIsInviteCodeValid] = React.useState(false);

    // 支付返回后恢复的报告数据
    const [restoredReport, setRestoredReport] = React.useState<{ person1Data: any; person2Data: any; aiAnswer: string; relationshipType: string } | null>(null);

    // 微信浏览器检测
    const [isWeChat, setIsWeChat] = React.useState(false);

    // 监听表单关系类型变化
    const selectedRelationship = Form.useWatch('relationshipType', form) || 'couple';

    // 检测是否在微信浏览器中
    React.useEffect(() => {
        setIsWeChat(isWeChatBrowser());
    }, []);

    // 检查 URL 参数：支付返回后恢复报告
    React.useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const orderId = urlParams.get('order');

      if (orderId) {
        // 从后端获取报告数据
        fetch(`/api/xunhupay-report?tradeNo=${orderId}`)
          .then(res => res.json())
          .then(data => {
            if (data.compressed && data.data) {
              // 解压数据
              const reportData = decompressData(data.data);
              if (reportData) {
                setRestoredReport(reportData);
                setShowReport(true);
                setIsInviteCodeValid(true); // 支付返回后自动解锁
                message.success('报告已恢复，欢迎回来！');
              }
            } else {
              // 报告数据不存在，可能是过期了
              message.error('报告数据已过期，请重新生成');
            }
          })
          .catch(err => {
            console.error('恢复报告失败:', err);
            message.error('恢复报告失败，请重新生成');
          });
        // 注意：不清理 URL 参数，保留 order 参数以便刷新后仍能恢复
      }
    }, []);

    // 构建城市选项
    const cityOptions = React.useMemo(() => CITY_OPTIONS, []);

    // 处理城市选择
    const handleCityChange = (fieldName: string) => (_cityName: string, option: any) => {
        const selectedCity = option.city as City;
        const isFirstPerson = fieldName === 'city1';
        form.setFieldValue(isFirstPerson ? 'longitude1' : 'longitude2', selectedCity.longitude);
        form.setFieldValue(isFirstPerson ? 'latitude1' : 'latitude2', selectedCity.latitude);
    };

    // 排盘逻辑（单人）
    const performPaiPan = (birthInfo: {
        year: number;
        month: number;
        day: number;
        hour: number;
        minute: number;
        longitude: number;
        gender: 'male' | 'female';
        distinguishZiShi?: boolean;
    }) => {
        const { year: inputYear, month: inputMonth, day: inputDay, hour: inputHour, minute: inputMinute, longitude, gender } = birthInfo;

        // 计算真太阳时
        const standard = dayjs(`${inputYear}-${inputMonth.toString().padStart(2, '0')}-${inputDay.toString().padStart(2, '0')} ${inputHour.toString().padStart(2, '0')}:${inputMinute.toString().padStart(2, '0')}:00`);
        const trueSolar = toTrueSolarTimeWithMinutes(standard, longitude, 8);

        // 标准时间参数
        let standardYear = standard.year();
        let standardMonth = standard.month() + 1;
        let standardDay = standard.date();
        const standardHour = standard.hour();

        // ===== 子时处理逻辑 =====
        const { distinguishZiShi } = birthInfo;
        const isInZiShi = standardHour === 23 || standardHour === 0;

        if (isInZiShi) {
            if (distinguishZiShi) {
                // 现代计法：区分早晚子时
                if (standardHour === 23) {
                    console.log('🕐 早子时（23:00-24:00），日柱使用当天');
                } else if (standardHour === 0) {
                    console.log('🕐 晚子时（00:00-01:00），日柱使用第二天');
                }
            } else {
                // 传统计法：23:00-01:00 统一算第二天
                if (standardHour === 23) {
                    const nextDay = standard.add(1, 'day');
                    standardYear = nextDay.year();
                    standardMonth = nextDay.month() + 1;
                    standardDay = nextDay.date();
                    console.log('🕐 传统子时（23:00-24:00），日柱推进到第二天:', `${standardYear}-${standardMonth}-${standardDay}`);
                } else {
                    console.log('🕐 传统子时（00:00-01:00），日柱已是第二天');
                }
            }
        }

        // 真太阳时参数
        const year = trueSolar.year();
        const month = trueSolar.month() + 1;
        const day = trueSolar.date();
        const trueSolarHour = trueSolar.hour();
        const trueSolarMinute = trueSolar.minute();

        // 计算行为模型四柱
        const solarDayStandard = SolarDay.fromYmd(standardYear, standardMonth, standardDay);
        const lunarDayStandard = solarDayStandard.getLunarDay();
        const solarObj = Solar.fromDate(standard.toDate());
        const lunarObj = solarObj.getLunar();
        const solarHourTrue = SolarTime.fromYmdHms(year, month, day, trueSolarHour, trueSolarMinute, 0);
        const lunarHourTrue = solarHourTrue.getLunarHour();

        // 年柱按立春时刻精确切换
        const sixtyCycleYear = lunarObj.getYearInGanZhiExact();
        let sixtyCycleMonth = lunarDayStandard.getMonthSixtyCycle().getName();
        const sixtyCycleDay = lunarDayStandard.getSixtyCycle().getName();
        const sixtyCycleHour = lunarHourTrue.getSixtyCycle().getName();

        // 月柱节气精确判断
        try {
            const currentJieQi = lunarObj.getCurrentJieQi();

            if (currentJieQi && currentJieQi.isJie()) {
                const birthTimestamp = standard.valueOf();
                const jieQiSolar = currentJieQi.getSolar();
                const jieQiTimestamp = new Date(
                    jieQiSolar.getYear(),
                    jieQiSolar.getMonth() - 1,
                    jieQiSolar.getDay(),
                    jieQiSolar.getHour(),
                    jieQiSolar.getMinute(),
                    jieQiSolar.getSecond()
                ).getTime();

                if (birthTimestamp < jieQiTimestamp) {
                    const prevJieQi = lunarObj.getPrevJieQi();
                    if (prevJieQi) {
                        const prevJieQiSolar = prevJieQi.getSolar();
                        const prevSolar = Solar.fromYmd(
                            prevJieQiSolar.getYear(),
                            prevJieQiSolar.getMonth(),
                            prevJieQiSolar.getDay()
                        );
                        sixtyCycleMonth = prevSolar.getLunar().getMonthInGanZhi();
                    }
                }
            }
        } catch (error) {
            console.error('⚠️ 月柱节气精确判断失败:', error);
        }

        // 计算阶段趋势
        const daYun = calculateDaYunWithAge(standardYear, standardMonth, standardDay, standardHour, inputMinute, sixtyCycleYear, sixtyCycleMonth, gender, 8);

        // 计算关系结构模型参数
        let validHour = Math.floor(trueSolarHour);
        if (validHour < 0) validHour = 0;
        if (validHour > 23) validHour = 23;
        const birthdayStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        const shiChenIndex = hourToShiChenIndex(validHour);

        return {
            fourPillars: {
                year: sixtyCycleYear,
                month: sixtyCycleMonth,
                day: sixtyCycleDay,
                hour: sixtyCycleHour
            },
            daYun,
            ziWeiParams: {
                birthday: birthdayStr,
                birthTime: shiChenIndex,
                gender: (gender === 'male' ? '男' : '女') as '男' | '女'
            }
        };
    };

    // 提交表单 - 所有计算和请求同步阻塞式执行
    const onFinish = async (values: any) => {
        if (reportStreaming) return;
        let verifiedForRequest = isInviteCodeValid;
        console.log('表单数据:', values);

        // 验证数据完整性
        if (!values.birthDateTime1 || !values.birthDateTime2) {
            message.error('请填写完整的出生信息');
            return;
        }

        // 如果填写了邀请码，先验证
        if (inviteCode.trim() && !isInviteCodeValid) {
            try {
                const resp = await fetch('/api/invite-code', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'verify',
                        code: inviteCode.trim(),
                        type: 'hepan'
                    })
                });
                const data = await resp.json();
                if (resp.ok && data.valid) {
                    verifiedForRequest = true;
                    setIsInviteCodeValid(true);
                } else {
                    message.error(data.error || '邀请码无效');
                    setIsInviteCodeValid(false);
                    return; // 验证失败，不继续生成报告
                }
            } catch (error) {
                message.error('邀请码验证失败，请稍后重试');
                return;
            }
        }

        setAiLoading(true);
        setReportToken('');
        setReportRedeemed(false);
        setAiAnswer('');

        try {
            // ========== 第一人数据计算 ==========
            const date1 = values.birthDateTime1.date;
            const result1 = performPaiPan({
                year: date1.year(),
                month: date1.month() + 1,
                day: date1.date(),
                hour: date1.hour(),
                minute: date1.minute(),
                longitude: Number(values.longitude1),
                gender: values.gender1,
                distinguishZiShi: values.distinguishZiShi1
            });

            // 同步计算关系结构数据
            const ziWeiData1 = calculateZiWeiData(
                result1.ziWeiParams.birthday,
                result1.ziWeiParams.birthTime,
                result1.ziWeiParams.gender
            );

            console.log('ziWeiData1', ziWeiData1)

            // ========== 第二人数据计算 ==========
            const date2 = values.birthDateTime2.date;
            const result2 = performPaiPan({
                year: date2.year(),
                month: date2.month() + 1,
                day: date2.date(),
                hour: date2.hour(),
                minute: date2.minute(),
                longitude: Number(values.longitude2),
                gender: values.gender2,
                distinguishZiShi: values.distinguishZiShi2
            });

            // 同步计算关系结构数据
            const ziWeiData2 = calculateZiWeiData(
                result2.ziWeiParams.birthday,
                result2.ziWeiParams.birthTime,
                result2.ziWeiParams.gender
            );
             console.log('ziWeiData2', ziWeiData2)

            // 检查关系结构数据计算结果
            if (!ziWeiData1 || !ziWeiData2) {
                throw new Error('关系结构模型计算失败，请检查出生信息');
            }

            // ========== 调用真实 API 生成报告 ==========
            const person1FullData = {
                ...result1,
                birthInfo: {
                    solar: values.birthDateTime1.date.format('YYYY-MM-DD HH:mm'),
                    city: values.city1,
                    longitude: values.longitude1,
                    latitude: values.latitude1
                },
                gender: values.gender1,
                mbti: values.mbti1,
                ziWeiData: ziWeiData1,
                name: values.name1
            };
            const person2FullData = {
                ...result2,
                birthInfo: {
                    solar: values.birthDateTime2.date.format('YYYY-MM-DD HH:mm'),
                    city: values.city2,
                    longitude: values.longitude2,
                    latitude: values.latitude2
                },
                gender: values.gender2,
                mbti: values.mbti2,
                ziWeiData: ziWeiData2,
                name: values.name2
            };

            const response = await runReportStream('/api/hepan', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    person1: person1FullData,
                    person2: person2FullData,
                    relationshipType: values.relationshipType,
                    userQuestion: values.userQuestion,
                    relationshipDetail: values.relationshipDetail,
                    enableCompatibilityScore: values.enableCompatibilityScore,
                })
            }, (text) => {
                setAiAnswer(text);
                setRelationshipType(values.relationshipType);
                setPerson1Data(person1FullData);
                setPerson2Data(person2FullData);
                setShowReport(true);
                setAiLoading(false);
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `请求失败: ${response.status}`);
            }

            const data = await response.json();

            // 解压报告数据获取 answer
            let answer = (data.access === 'full' ? data.answer : data.previewAnswer || data.answer) || '';
            if (data.compressedReport) {
                const reportData = decompressData(data.compressedReport);
                if (reportData) {
                    answer = reportData.aiAnswer || '';
                }
            }

            setReportToken(data.reportToken || '');
            if (data.reportToken && verifiedForRequest && inviteCode.trim()) {
                const redeemed = await fetch('/api/invite-code', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'redeem', type: 'hepan', code: inviteCode.trim(), reportToken: data.reportToken }),
                });
                const unlocked = await redeemed.json();
                if (!redeemed.ok) { setIsInviteCodeValid(false); message.error(unlocked.error || '邀请码解锁失败'); }
                else { answer = unlocked.answer; setReportRedeemed(true); }
            }
            // 设置报告数据
            setAiAnswer(answer);
            setRelationshipType(values.relationshipType);
            setCompressedReport(data.compressedReport || '');
            setPerson1Data(person1FullData);
            setPerson2Data(person2FullData);
            setShowReport(true);
        } catch (error) {
            console.error('排盘或API调用失败:', error);
            message.error(error instanceof Error ? error.message : '生成报告失败，请稍后重试');
        } finally {
            setAiLoading(false);
        }
    };

    return (
        <ConfigProvider locale={zhCN}>
            {/* 微信浏览器引导蒙层 */}
            <WeChatGuide visible={isWeChat} />

            {/* AI 加载动画 */}
            <AILoadingAnimation
                visible={aiLoading}
                mode="hepan"
                title={isFollowUpLoading ? '正在解读这次关系追问...' : '正在生成双人关系说明书...'}
                subtitle={isFollowUpLoading ? '结合原报告、历史追问与双方关系结构继续推演' : '正在融合双方命盘与关系结构'}
            />

            {/* 报告页面 */}
            {showReport && (person1Data && person2Data || restoredReport) ? (
                <HepanReport
                    person1Data={person1Data}
                    person2Data={person2Data}
                    relationshipType={relationshipType}
                    aiAnswer={aiAnswer}
                    onBack={() => {
                        // 如果是已解锁用户，弹窗确认是否已保存
                        if (isInviteCodeValid || restoredReport) {
                            Modal.confirm({
                                title: '确认返回？',
                                content: '为保护您的个人隐私，本报告不会保存在服务器。请确认已将报告保存为图片，否则离开后将无法再次查看。',
                                okText: '已保存，返回',
                                cancelText: '取消',
                                onOk: () => {
                                    setShowReport(false);
                                    // 返回时清理 URL 中的订单参数
                                    if (window.location.search.includes('order=')) {
                                        window.history.replaceState({}, '', window.location.pathname);
                                    }
                                }
                            });
                        } else {
                            setShowReport(false);
                            // 返回时清理 URL 中的订单参数
                            if (window.location.search.includes('order=')) {
                                window.history.replaceState({}, '', window.location.pathname);
                            }
                        }
                    }}
                    initialUnlocked={siteConfig.isFreeMode || reportAccess === 'full' || (reportToken ? reportRedeemed : isInviteCodeValid)}
                    preValidatedInviteCode={!reportToken && !streamReportToken && isInviteCodeValid ? inviteCode : undefined}
                    restoredReport={restoredReport}
                    compressedReport={compressedReport}
                    reportToken={reportToken || streamReportToken}
                    generating={reportStreaming}
                    generationError={streamError}
                    onFollowUpLoadingChange={(loading) => {
                        setIsFollowUpLoading(loading);
                        setAiLoading(false);
                    }}
                />
            ) : (
                /* 表单页面 */
                <div style={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                    padding: '40px 20px'
                }}>
                    <div style={{
                        maxWidth: '1200px',
                        margin: '0 auto'
                    }}>
                        {/* 返回按钮 */}
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => window.location.href = '/'}
                            style={{
                                marginBottom: '24px',
                                background: 'rgba(255, 255, 255, 0.9)',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            返回首页
                        </Button>

                        {/* 标题区域 */}
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '40px'
                        }}>
                            <h1 className="hepan-title">
                                双人关系图谱 · 深度契合分析
                            </h1>
                            <p className="hepan-subtitle">
                                多维度解析两人互动模式，提供科学相处优化指南
                            </p>
                            <div className="hepan-description">
                                <div>行为模型· 关系结构· 数据图谱</div>
                            </div>
                        </div>

                        {/* 表单区域 */}
                        <div className="hepan-form-container">
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                            >
                                <div className="hepan-form-grid">
                                    {/* 第一人信息 */}
                                    <div className="hepan-person-card">
                                        <h3 className="hepan-card-title">
                                            第一人信息
                                        </h3>

                                        <Form.Item
                                            name="name1"
                                            label={
                                                <span>
                                                    称呼
                                                    <Tooltip title={'填写称呼（如：小明），报告会更亲切。不填则显示为「第一人」'}>
                                                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                                                    </Tooltip>
                                                </span>
                                            }
                                        >
                                            <Input
                                                placeholder="例如：小明"
                                                maxLength={10}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="birthDateTime1"
                                            label="出生时间"
                                            rules={[{ required: true, message: '请选择出生时间' }]}
                                        >
                                            <BirthDateTimePicker />
                                        </Form.Item>

                                        <Form.Item
                                            name="gender1"
                                            label="性别"
                                            rules={[{ required: true, message: '请选择性别' }]}
                                        >
                                            <Select
                                                placeholder="请选择性别"
                                                options={[
                                                    { value: 'male', label: '男' },
                                                    { value: 'female', label: '女' },
                                                ]}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="mbti1"
                                            label="MBTI（可选）"
                                        >
                                            <Select
                                                placeholder="请选择或输入MBTI类型"
                                                allowClear
                                                showSearch
                                                options={MBTI_OPTIONS}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="city1"
                                            label="出生城市"
                                            rules={[{ required: true, message: '请选择出生城市' }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="请选择城市"
                                                options={cityOptions}
                                                onChange={handleCityChange('city1')}
                                                optionFilterProp="searchText"
                                                filterOption={cityFilterOption}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="longitude1"
                                            label="经度"
                                            hidden
                                        >
                                            <Select />
                                        </Form.Item>

                                        <Form.Item
                                            name="latitude1"
                                            label="纬度"
                                            hidden
                                        >
                                            <Select />
                                        </Form.Item>

                                        <Form.Item
                                            name="distinguishZiShi1"
                                            valuePropName="checked"
                                            initialValue={false}
                                            style={{ marginBottom: 0, marginTop: 8 }}
                                        >
                                            <Checkbox>
                                                <span style={{ fontSize: '14px' }}>
                                                    区分早晚子时
                                                </span>
                                                <Tooltip title={'子时（23:00-01:00）传统排法统一算第二天。勾选后23点算当天，0点算第二天。仅当出生时间在23:00-01:00时有影响。'}>
                                                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                                                </Tooltip>
                                            </Checkbox>
                                        </Form.Item>
                                    </div>

                                    {/* 第二人信息 */}
                                    <div className="hepan-person-card">
                                        <h3 className="hepan-card-title">
                                            第二人信息
                                        </h3>

                                        <Form.Item
                                            name="name2"
                                            label={
                                                <span>
                                                    称呼
                                                    <Tooltip title={'填写称呼（如：小红），报告会更亲切。不填则显示为「第二人」'}>
                                                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                                                    </Tooltip>
                                                </span>
                                            }
                                        >
                                            <Input
                                                placeholder="例如：小红"
                                                maxLength={10}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="birthDateTime2"
                                            label="出生时间"
                                            rules={[{ required: true, message: '请选择出生时间' }]}
                                        >
                                            <BirthDateTimePicker />
                                        </Form.Item>

                                        <Form.Item
                                            name="gender2"
                                            label="性别"
                                            rules={[{ required: true, message: '请选择性别' }]}
                                        >
                                            <Select
                                                placeholder="请选择性别"
                                                options={[
                                                    { value: 'male', label: '男' },
                                                    { value: 'female', label: '女' },
                                                ]}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="mbti2"
                                            label="MBTI（可选）"
                                        >
                                            <Select
                                                placeholder="请选择或输入MBTI类型"
                                                allowClear
                                                showSearch
                                                options={MBTI_OPTIONS}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="city2"
                                            label="出生城市"
                                            rules={[{ required: true, message: '请选择出生城市' }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="请选择城市"
                                                options={cityOptions}
                                                onChange={handleCityChange('city2')}
                                                optionFilterProp="searchText"
                                                filterOption={cityFilterOption}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            name="longitude2"
                                            label="经度"
                                            hidden
                                        >
                                            <Select />
                                        </Form.Item>

                                        <Form.Item
                                            name="latitude2"
                                            label="纬度"
                                            hidden
                                        >
                                            <Select />
                                        </Form.Item>

                                        <Form.Item
                                            name="distinguishZiShi2"
                                            valuePropName="checked"
                                            initialValue={false}
                                            style={{ marginBottom: 0, marginTop: 8 }}
                                        >
                                            <Checkbox>
                                                <span style={{ fontSize: '14px' }}>
                                                    区分早晚子时
                                                </span>
                                                <Tooltip title={'子时（23:00-01:00）传统排法统一算第二天。勾选后23点算当天，0点算第二天。仅当出生时间在23:00-01:00时有影响。'}>
                                                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
                                                </Tooltip>
                                            </Checkbox>
                                        </Form.Item>
                                    </div>
                                </div>

                                {/* 关系类型选择 */}
                                <div className="hepan-relationship-section">
                                    <h3 className="hepan-section-title">关系类型</h3>
                                    <Form.Item
                                        name="relationshipType"
                                        rules={[{ required: true, message: '请选择关系类型' }]}
                                        initialValue="couple"
                                    >
                                        <Select
                                            placeholder="请选择关系类型"
                                            options={[
                                                { value: 'couple', label: '恋人 / 夫妻' },
                                                { value: 'ambiguous', label: '暧昧期 / 友达以上' },
                                                { value: 'friend', label: '朋友 / 知己' },
                                                { value: 'partner', label: '搞钱搭子 / 合伙人' },
                                                { value: 'parent_child', label: '父母 / 子女' },
                                                { value: 'sibling', label: '兄弟姐妹' },
                                            ]}
                                        />
                                    </Form.Item>

                                    {/* 动态说明卡片 */}
                                    <div className="hepan-relationship-info">
                                        {selectedRelationship === 'couple' && (
                                            <div className="relationship-card couple">
                                                <h4>恋人/夫妻</h4>
                                                <p>深度解析你们的关系形成因素类型、日常相处模式、吵架急救包、好好说话指南、关系活力保鲜剂，以及未来三年感情趋势。</p>
                                            </div>
                                        )}
                                        {selectedRelationship === 'ambiguous' && (
                                            <div className="relationship-card ambiguous">
                                                <h4>暧昧期/友达以上</h4>
                                                <p>一针见血剖析当前磁场、滤镜与现实差异、下头瞬间与避雷针、直球还是拉扯指南、定心丸配方，以及未来一年窗口期预判。</p>
                                            </div>
                                        )}
                                        {selectedRelationship === 'friend' && (
                                            <div className="relationship-card friend">
                                                <h4>朋友/知己</h4>
                                                <p>解码友谊基底、性格拼图与默契度、友谊翻船预警、走心夜聊解码器、情绪价值充值站，以及未来三年友谊路标。</p>
                                            </div>
                                        )}
                                        {selectedRelationship === 'partner' && (
                                            <div className="relationship-card partner">
                                                <h4>搞钱搭子/合伙人</h4>
                                                <p>搞钱基因鉴定、搞钱分工地图、散伙红线与防坑预警、商业沟通解码、动力泵与定心丸，以及未来三年搞钱风口预判。</p>
                                            </div>
                                        )}
                                        {selectedRelationship === 'parent_child' && (
                                            <div className="relationship-card family">
                                                <h4>👨‍👩‍👧父母/子女</h4>
                                                <p>亲子羁绊底层代码、代沟与滤镜拆解、防雷防爆指南、跨服聊天解码器、安全感储蓄罐，以及未来三年亲子趋势路标。</p>
                                            </div>
                                        )}
                                        {selectedRelationship === 'sibling' && (
                                            <div className="relationship-card sibling">
                                                <h4>兄弟姐妹</h4>
                                                <p>手足羁绊底层代码、手足相处模式、手足翻船预警、手足沟通解码器、手足互助指南，以及未来三年手足趋势路标。</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* 关系背景 */}
                                <div className="hepan-relationship-section">
                                    <h3 className="hepan-section-title">
                                        关系背景
                                    </h3>
                                    {/* 重要提示卡片 */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                                        border: '2px solid #8b5cf6',
                                        borderRadius: '12px',
                                        padding: '16px 20px',
                                        marginBottom: '16px',
                                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.15)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px'
                                        }}>
                                            <div>
                                                <div style={{
                                                    fontSize: '15px',
                                                    fontWeight: 700,
                                                    color: '#6b21a8',
                                                    marginBottom: '8px'
                                                }}>
                                                    这一步非常重要，直接影响报告质量！
                                                </div>
                                                <div style={{
                                                    fontSize: '14px',
                                                    color: '#7c3aed',
                                                    lineHeight: '1.8'
                                                }}>
                                                    行为模型和数据图谱只能算出你们关系的"天花板"和"地雷阵"，但现实中你们是相爱相杀还是老死不相往来，<strong>取决于你们当下的选择</strong>。
                                                    <br /><br />
                                                    <strong style={{ color: '#5b21b6' }}>不要试图让系统去"猜"</strong>你们认识多久了、有没有吵架。
                                                    <strong style={{ color: '#5b21b6' }}>直接把核心痛点填进来</strong>（例如：异地恋极度内耗、合伙分赃不均）。
                                                    <br /><br />
                                                    <span style={{ fontWeight: 600 }}>你敢给出最残酷的现实，算法就敢给你最不留情面的破局指南。</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <Form.Item
                                        name="relationshipDetail"
                                        style={{ marginTop: '16px', marginBottom: 0 }}
                                    >
                                        <Input.TextArea
                                            placeholder={`例如：我们是大学同学，认识5年了。最近因为创业理念不合经常吵架，TA比较激进想快速扩张，我比较保守想稳扎稳打。我们目前合伙开了一家咖啡店，但最近三个月几乎每周都要争论一次...`}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                fontSize: '14px',
                                                border: '1px solid #e8e8e8',
                                                borderRadius: '8px',
                                                outline: 'none',
                                                transition: 'all 0.3s',
                                                background: '#fafafa',
                                                minHeight: '100px',
                                                resize: 'vertical'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#667eea';
                                                e.target.style.background = '#fff';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e8e8e8';
                                                e.target.style.background = '#fafafa';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                    </Form.Item>
                                    <p className="hepan-hint">详细描述你们的相处模式、矛盾点、共同经历等，AI 会给出更精准的分析</p>
                                </div>

                                {/* 当前最关心的问题 */}
                                <div className="hepan-question-section">
                                    <Form.Item
                                        name="userQuestion"
                                        label={
                                            <span style={{ fontSize: '16px', fontWeight: 600, color: '#333', marginTop: '16px' }}>
                                                当前最关心的问题（可选）
                                            </span>
                                        }
                                    >
                                        <Input
                                            placeholder="例如：我们适合一起创业吗？/ 最近总吵架怎么办？"
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px',
                                                fontSize: '15px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '8px',
                                                outline: 'none',
                                                transition: 'all 0.3s'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#667eea';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#d9d9d9';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                    </Form.Item>
                                    <p className="hepan-hint">AI 将单开一章专门解读你的问题</p>
                                </div>

                                {/* 邀请码输入 */}
                                <div style={{ marginTop: '24px', padding: '16px', background: 'rgba(212, 175, 106, 0.08)', borderRadius: '12px', border: '1px dashed #d4af6a' }}>
                                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                                        <GiftOutlined style={{ color: '#d4af6a', marginRight: 6 }} />
                                        有邀请码？输入即可免费解锁完整报告
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#999', marginBottom: '12px' }}>
                                        没有邀请码也可以直接生成，免费查看前两个章节
                                    </div>
                                    <Input
                                        placeholder="请输入邀请码"
                                        value={inviteCode}
                                        onChange={(e) => {
                                            const code = e.target.value.toUpperCase();
                                            setInviteCode(code);
                                            // 清空验证状态
                                            if (isInviteCodeValid) {
                                                setIsInviteCodeValid(false);
                                            }
                                        }}
                                        style={{
                                            height: '44px',
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            letterSpacing: '2px',
                                            textTransform: 'uppercase',
                                            borderColor: isInviteCodeValid ? '#52c41a' : '#d9d9d9'
                                        }}
                                    />
                                    {isInviteCodeValid && (
                                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#52c41a' }}>
                                            ✓ 邀请码已填写，生成报告后将自动解锁完整内容
                                        </div>
                                    )}
                                </div>

                                {/* 契合度评分开关 */}
                                <div className="hepan-compatibility-section" style={{ marginTop: '24px',  marginBottom: '24px',padding: '20px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)' }}>
                                    <Form.Item
                                        name="enableCompatibilityScore"
                                        valuePropName="checked"
                                        initialValue={true}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Checkbox>
                                            <span style={{ fontSize: '16px', fontWeight: 600 }}>
                                                计算契合度评分
                                            </span>
                                            <Tooltip title={'AI 会从关系形成因素、性格、关系契合、长期潜力等维度计算综合契合度分数（1-100分）'}>
                                                <QuestionCircleOutlined style={{ marginLeft: 6}} />
                                            </Tooltip>
                                        </Checkbox>
                                    </Form.Item>
                                    <p className="hepan-hint" style={{ marginTop: '10px', marginLeft: '24px', fontSize: '13px' }}>
                                        从关系形成因素、性格、关系契合、长期潜力等维度给出综合评分
                                    </p>
                                </div>

                                {/* 提交按钮 */}
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={aiLoading}
                                        className="hepan-submit-btn"
                                        style={{
                                            width: '100%',
                                            height: '48px',
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                                        }}
                                    >
                                        {aiLoading ? '生成报告中...' : '生成关系说明书'}
                                    </Button>
                                    <div style={{
                                        textAlign: 'center',
                                        marginTop: '12px',
                                        fontSize: '12px',
                                        color: '#999'
                                    }}>
                                        本测评基于心理学逻辑建模，结论仅供人际关系参考，不作为人生决策依据
                                    </div>
                                </Form.Item>
                            </Form>
                        </div>
                    </div>
                </div>
            )}
        </ConfigProvider>
    );
};

export default Hepan;
